import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { fmtErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Check, Copy, Download, Eye, Link2, Loader2, Trash2 } from "lucide-react";
import { useTranslation } from "react-i18next";

const PRESETS = [
  { key: "7", labelKey: "preset7" },
  { key: "30", labelKey: "preset30" },
  { key: "90", labelKey: "preset90" },
  { key: "custom", labelKey: "presetCustom" },
];

export function ShareDialog({ target, onClose, onChanged }) {
  const { t } = useTranslation();
  const [preset, setPreset] = useState("30");
  const [customDate, setCustomDate] = useState("");
  const [permission, setPermission] = useState("view_download");
  const [creating, setCreating] = useState(false);
  const [created, setCreated] = useState(null);
  const [copied, setCopied] = useState(false);

  // Dialog stays mounted between shares — reset state whenever a new target opens it,
  // otherwise the previous share's result screen (and its URL) is shown for the new target.
  useEffect(() => {
    setCreated(null);
    setCopied(false);
    setPreset("30");
    setCustomDate("");
    setPermission("view_download");
  }, [target]);

  const expiresAt = () => {
    if (preset === "custom") {
      if (!customDate) return null;
      return new Date(`${customDate}T23:59:59`).toISOString();
    }
    return new Date(Date.now() + Number(preset) * 86400000).toISOString();
  };

  const create = async () => {
    const exp = expiresAt();
    if (!exp) {
      toast.error(t("shareDialog.pickDateErr"));
      return;
    }
    setCreating(true);
    try {
      const body = {
        target_type: target.type === "folders" ? "folder" : target.type,
        expires_at: exp,
        permission,
        ...(target.type === "file"
          ? { file_id: target.file.id }
          : target.folders
            ? { scope: target.scope, owner_id: target.ownerId || undefined, folders: target.folders }
            : { scope: target.scope, owner_id: target.ownerId || undefined, folder: target.folder }),
      };
      const { data } = await api.post("/drive/shares", body);
      setCreated(data);
      onChanged?.();
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setCreating(false);
    }
  };

  const copy = () => {
    navigator.clipboard.writeText(created.url).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    }).catch(() => toast.error(t("shareDialog.copyFailed")));
  };

  const revokeNow = async () => {
    try {
      await api.delete(`/drive/shares/${created.id}`);
      toast.success(t("sharedLinks.revokedToast"));
      onChanged?.();
      onClose();
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  const label = target?.type === "file" ? target.file?.filename : (target?.folders ? target.folders.join(", ") : target?.folder);

  return (
    <Dialog open={!!target} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="share-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Link2 className="h-4 w-4 shrink-0" /> <span className="min-w-0 truncate">{t("shareDialog.title", { what: target?.type === "file" ? t("shareDialog.whatFile") : t("shareDialog.whatFolder"), label })}</span></DialogTitle>
        </DialogHeader>

        {!created ? (
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>{t("shareDialog.expirationL")}</Label>
              <div className="flex flex-wrap gap-2" data-testid="share-presets">
                {PRESETS.map((p) => (
                  <Button key={p.key} size="sm" variant={preset === p.key ? "default" : "outline"} className={preset === p.key ? "bg-slate-900" : ""} onClick={() => setPreset(p.key)} data-testid={`share-preset-${p.key}`}>
                    {t(`shareDialog.${p.labelKey}`)}
                  </Button>
                ))}
              </div>
              {preset === "custom" && (
                <Input type="date" min={new Date(Date.now() + 86400000).toISOString().slice(0, 10)} value={customDate} onChange={(e) => setCustomDate(e.target.value)} className="mt-2 w-56" data-testid="share-custom-date" />
              )}
            </div>
            <div className="space-y-1.5">
              <Label>{t("shareDialog.permissionL")}</Label>
              <div className="grid grid-cols-2 gap-2">
                <button type="button" onClick={() => setPermission("view")} className={`rounded-md border p-3 text-left transition-colors ${permission === "view" ? "border-sky-500 bg-sky-50" : "hover:bg-slate-50"}`} data-testid="share-perm-view">
                  <div className="flex items-center gap-1.5 text-sm font-semibold"><Eye className="h-4 w-4" /> {t("shareDialog.viewOnly")}</div>
                  <div className="mt-0.5 text-xs text-slate-500">{t("shareDialog.viewOnlyDesc")}</div>
                </button>
                <button type="button" onClick={() => setPermission("view_download")} className={`rounded-md border p-3 text-left transition-colors ${permission === "view_download" ? "border-sky-500 bg-sky-50" : "hover:bg-slate-50"}`} data-testid="share-perm-download">
                  <div className="flex items-center gap-1.5 text-sm font-semibold"><Download className="h-4 w-4" /> {t("shareDialog.viewDownload")}</div>
                  <div className="mt-0.5 text-xs text-slate-500">{t("shareDialog.viewDownloadDesc")}</div>
                </button>
              </div>
            </div>
            <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
              {t("shareDialog.warnAnyone")}
            </div>
          </div>
        ) : (
          <div className="space-y-4" data-testid="share-result">
            <div className="space-y-1.5">
              <Label>{t("shareDialog.shareUrl")}</Label>
              <div className="flex items-center gap-2 overflow-hidden rounded-md border bg-slate-50 px-3 py-2">
                <span className="min-w-0 flex-1 cursor-text select-all truncate font-mono text-xs" title={created.url} data-testid="share-result-url">{created.url}</span>
                <Button size="sm" variant="outline" className="h-7 shrink-0" onClick={copy} data-testid="share-copy-url">
                  {copied ? <Check className="mr-1 h-3.5 w-3.5" /> : <Copy className="mr-1 h-3.5 w-3.5" />} {copied ? t("shareDialog.copied") : t("shareDialog.copy")}
                </Button>
              </div>
            </div>
            <div className="rounded-md border px-3 py-2.5 text-xs text-slate-600" data-testid="share-result-summary">
              <div><b>{created.target_type === "file" ? t("common.file") : t("common.folder")}:</b> {created.label}</div>
              <div className="mt-1"><b>{t("shareDialog.expiresB")}</b> {new Date(created.expires_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })} · <b>{t("shareDialog.permissionL")}:</b> {created.permission === "view_download" ? t("shareDialog.viewDownload") : t("shareDialog.viewOnly")}</div>
            </div>
          </div>
        )}

        <DialogFooter>
          {!created ? (
            <Button onClick={create} disabled={creating || (preset === "custom" && !customDate)} className="bg-slate-900 hover:bg-slate-800" data-testid="share-create-submit">
              {creating && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} {t("shareDialog.createLink")}
            </Button>
          ) : (
            <>
              <Button variant="outline" className="text-rose-600" onClick={revokeNow} data-testid="share-revoke-now">
                <Trash2 className="mr-1.5 h-4 w-4" /> {t("shareDialog.revokeNow")}
              </Button>
              <Button onClick={onClose} className="bg-slate-900 hover:bg-slate-800" data-testid="share-done">{t("shareDialog.done")}</Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
